import { computed, inject, type Signal } from '@angular/core';
import { isProposedSchedule } from '@app/+authenticated/+schedule/shared/auto-schedule/proposed-shift.adapter';
import { useRepaintOnChange } from '@app/+authenticated/+schedule/shared/schedule-items/use-repaint-on-change';
import { FindCoverService } from '@app/+modals/schedule/schedule-find-cover/find-cover.service';
import type { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import type { AppState } from '@reducers/interfaces';
import type { DepartmentModel } from '@reducers/orm/department/department.model';
import { getDepartmentState } from '@reducers/orm/department/department.service';
import type { ComplianceViolation } from '@reducers/orm/schedule-compliance/schedule-compliance.model';
import { getScheduleViolations } from '@reducers/orm/schedule-compliance/schedule-compliance.selectors';
import type { ScheduleConflictModel } from '@reducers/orm/schedule-conflict/schedule-conflict.model';
import { getScheduleConflict } from '@reducers/orm/schedule-conflict/schedule-conflict.selectors';
import type { ScheduleModel } from '@reducers/orm/schedule/schedule.model';
import type { ShiftModel } from '@reducers/orm/shift/shift.model';
import { getShiftState, setShiftColor } from '@reducers/orm/shift/shift.service';
import type { TeamModel } from '@reducers/orm/team/team.model';
import { getTeamState } from '@reducers/orm/team/team.service';
import { selectAutoScheduleLockedDepartmentIds } from '@reducers/store/schedule/auto-schedule.selector';
import type { ScheduleItemModel } from '@reducers/store/schedule/schedule.n.model';

/**
 * Store data and derived state of one scheduled shift, keyed on the schedule's
 * ids: a store update re-selects only for the shift it concerns, and a new
 * schedule object with the same ids reuses the existing selectors.
 */
export function useScheduledShift(schedule: Signal<ScheduleModel>) {
  const store = inject<Store<AppState>>(Store);
  const findCover = inject(FindCoverService);

  const shiftId = computed(() => schedule().shift_id);
  const teamId = computed(() => schedule().team_id);
  const departmentId = computed(() => schedule().department_id);
  const userId = computed(() => schedule().user_id);
  const occurrenceId = computed(() => schedule().occurrence_id);

  // Raw entities keep their reference across unrelated store updates. The
  // mapped `getShift`, `getTeam` and `getDepartment` rebuild every entity of
  // their kind on any change (departments even on a shift change), which would
  // repaint every shift on the schedule.
  const rawShift = selectByKey<ShiftModel | undefined>(
    store,
    () => (state) => getShiftState(state).itemsById[shiftId()],
  );
  const shift = computed(() => {
    const selectedShift = rawShift();

    return selectedShift ? setShiftColor(selectedShift) : undefined;
  });
  const scheduledTeam = selectByKey<TeamModel | undefined>(
    store,
    () => (state) => getTeamState(state).itemsById[teamId()],
  );
  const scheduledDepartment = selectByKey<DepartmentModel | undefined>(
    store,
    () => (state) => getDepartmentState(state).itemsById[departmentId()],
  );
  const violation = selectByKey<ComplianceViolation | undefined>(store, () =>
    getScheduleViolations(parseInt(userId(), 10), occurrenceId()),
  );
  const conflicts = selectByKey<Dictionary<ScheduleConflictModel[]> | null>(store, () =>
    getScheduleConflict(userId(), occurrenceId()),
  );

  const lockedDepartmentIds = store.selectSignal(selectAutoScheduleLockedDepartmentIds);
  const isDepartmentLocked = computed(() => lockedDepartmentIds().includes(departmentId()));

  const isProposed = computed(() => isProposedSchedule(schedule()));
  const isLocked = computed(() => isProposed() || isDepartmentLocked());
  const isConflicted = computed(() => !!conflicts() || (violation()?.messages?.length ?? 0) > 0);

  const canFindCover = computed(() => {
    const item: Partial<ScheduleItemModel> = schedule();
    const allConflicts = Object.values(conflicts() ?? {}).flatMap((topicConflicts) => topicConflicts ?? []);

    return findCover.canFindCover({
      conflicts: allConflicts,
      canEdit: !!item.permissions?.canEdit,
      date: item.date,
    });
  });

  useRepaintOnChange([shift, scheduledTeam, scheduledDepartment, violation, conflicts, isDepartmentLocked]);

  return {
    shift,
    scheduledTeam,
    scheduledDepartment,
    violation,
    conflicts,
    isProposed,
    isLocked,
    isConflicted,
    canFindCover,
  };
}

/**
 * Selects through a selector that depends on signals. The selector, and the
 * store signal over it, are rebuilt only when the key signals change, so the
 * selector's memoization survives unrelated store updates.
 */
function selectByKey<T>(store: Store<AppState>, selector: () => (state: AppState) => T): Signal<T> {
  const selected = computed(() => store.selectSignal(selector()));

  return computed(() => selected()());
}
